import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Drop-in replacement for `<select className="form-control">` that lets the
 * user type to filter instead of scrolling a native option list.
 *
 * The API is deliberately identical to a native select — same `value`,
 * same `onChange(e)` where `e.target.value` is the chosen option's value, and
 * the options are passed as `<option>` children. That's what makes migrating a
 * page a one-word rename per control rather than a rewrite:
 *
 *     <select  className="form-control" value={v} onChange={...}>  →
 *     <SearchableSelect className="form-control" value={v} onChange={...}>
 *
 * The menu renders in a portal with fixed positioning because several of the
 * dropdowns live inside `.modal` (which is `overflow-y: auto`) and inside
 * `.table-container` (`overflow-x: auto`) — an absolutely-positioned menu would
 * be clipped by both.
 */

// Flatten JSX children into {value, label, disabled}. Conditional children
// ({cond && <option/>}) come through as false/null and are dropped.
function readOptions(children) {
  const out = [];
  React.Children.forEach(children, (child) => {
    if (!child || typeof child !== 'object' || child.type !== 'option') return;
    const { value, children: label, disabled } = child.props;
    const text = extractText(label);
    out.push({
      value: value !== undefined ? String(value) : text,
      label: text,
      disabled: !!disabled,
    });
  });
  return out;
}

// An <option>'s children can be a string, a number, or an array of fragments
// ("{l.label} — {l.size}"). Reduce whatever it is to searchable text.
function extractText(node) {
  if (node === null || node === undefined || node === false) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractText).join('');
  if (node.props && node.props.children !== undefined) return extractText(node.props.children);
  return '';
}

const MENU_MAX_HEIGHT = 280;

export default function SearchableSelect({
  value,
  onChange,
  children,
  className = 'form-control',
  style,
  disabled = false,
  placeholder = 'Select…',
  searchPlaceholder = 'Type to search…',
  id,
  name,
  required,
  title,
  // Every dropdown gets a search box, no matter how short the list — the
  // behaviour has to be predictable, and an operator who has learned to type
  // shouldn't hit a dropdown that silently ignores them. Set a number to
  // suppress it below that many options; 0 (the default) never suppresses.
  searchThreshold = 0,
  // Creatable mode: the operator may commit whatever they typed even when it
  // matches no option. Used for master lists flagged allow_user_add, which
  // previously rendered a bare <input list=datalist> — inconsistent across
  // browsers and visually unlike every other dropdown on the page.
  allowCustom = false,
  ...rest
}) {
  const options = useMemo(() => readOptions(children), [children]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [rect, setRect] = useState(null);

  const anchorRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const seededRef = useRef(false);
  // The listbox lives in a portal, so the combobox has to point at it by id
  // for screen readers to connect the two.
  const listboxId = `${useId()}-listbox`;

  const selected = options.find((o) => o.value === String(value ?? ''));
  const showSearch = options.length >= searchThreshold;
  // Typing on the closed control opens it and seeds the filter, so search works
  // without having to find the box first.
  const seedFromKey = (key) => {
    if (key.length !== 1 || key === ' ') return false;
    seededRef.current = true;
    setOpen(true);
    setQuery(key);
    setHighlight(0);
    return true;
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const place = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Flip above the control when there isn't room below it — matters for the
    // filter bars that sit near the bottom of a short page.
    const below = window.innerHeight - r.bottom;
    const above = r.top;
    const openUp = below < Math.min(MENU_MAX_HEIGHT, 200) && above > below;
    setRect({
      left: r.left,
      width: r.width,
      top: openUp ? undefined : r.bottom + 4,
      bottom: openUp ? window.innerHeight - r.top + 4 : undefined,
      maxHeight: Math.min(MENU_MAX_HEIGHT, (openUp ? above : below) - 12),
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    place();
    // Reposition rather than close on scroll/resize: closing on scroll makes
    // the control feel broken when the page moves under a trackpad.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (e) => {
      if (anchorRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // Don't wipe a query the user just typed to open the menu (see seedFromKey).
    if (seededRef.current) {
      seededRef.current = false;
    } else {
      setQuery('');
      const idx = options.findIndex((o) => o.value === String(value ?? ''));
      setHighlight(idx >= 0 ? idx : 0);
    }
    // Focus the search box on the next frame — it doesn't exist until the
    // portal has painted.
    const t = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Keep the highlighted row scrolled into view while arrowing through a long list.
  useEffect(() => {
    if (!open || !menuRef.current) return;
    const node = menuRef.current.querySelector(`[data-idx="${highlight}"]`);
    if (node && node.scrollIntoView) node.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  const commit = (opt) => {
    if (!opt || opt.disabled) return;
    setOpen(false);
    // Shaped like a native change event so call sites keep working unchanged.
    onChange?.({ target: { value: opt.value, name } });
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (!open) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
        e.preventDefault();
        setOpen(true);
      } else if (!e.ctrlKey && !e.metaKey && !e.altKey && seedFromKey(e.key)) {
        e.preventDefault();
      }
      return;
    }
    if (e.key === 'Escape') { e.preventDefault(); setOpen(false); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
      return;
    }
    if (e.key === 'Home') { e.preventDefault(); setHighlight(0); return; }
    if (e.key === 'End') { e.preventDefault(); setHighlight(filtered.length - 1); return; }
    if (e.key === 'Enter' || e.key === 'Tab') {
      if (filtered[highlight]) {
        e.preventDefault();
        commit(filtered[highlight]);
      } else if (canCreate) {
        // Nothing matched but the field accepts new values — take the text.
        e.preventDefault();
        commit({ value: trimmedQuery, label: trimmedQuery });
      }
    }
  };

  // Any keystroke resets the highlight to the first match, so Enter always
  // picks the most obvious result after typing.
  const onQuery = (e) => {
    setQuery(e.target.value);
    setHighlight(0);
  };

  // In creatable mode the stored value may be a free-typed string with no
  // matching option — show it rather than falling back to the placeholder.
  const displayLabel = selected
    ? selected.label
    : (allowCustom && value ? String(value) : '');

  const trimmedQuery = query.trim();
  const canCreate =
    allowCustom &&
    trimmedQuery !== '' &&
    !options.some((o) => o.label.toLowerCase() === trimmedQuery.toLowerCase());

  return (
    <>
      <div
        ref={anchorRef}
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-haspopup="listbox"
        aria-disabled={disabled || undefined}
        aria-required={required || undefined}
        title={title}
        tabIndex={disabled ? -1 : 0}
        className={`${className} ss-control${open ? ' ss-open' : ''}${disabled ? ' ss-disabled' : ''}`}
        style={style}
        onKeyDown={onKeyDown}
        onMouseDown={(e) => {
          if (disabled) return;
          e.preventDefault();      // don't steal focus from the search input
          anchorRef.current?.focus();
          setOpen((o) => !o);
        }}
        {...rest}
      >
        <span className={`ss-value${displayLabel ? '' : ' ss-placeholder'}`}>
          {displayLabel || placeholder}
        </span>
        <span className="ss-caret" aria-hidden="true" />
      </div>

      {open && rect && createPortal(
        <div
          ref={menuRef}
          id={listboxId}
          className="ss-menu"
          role="listbox"
          style={{
            left: rect.left,
            width: rect.width,
            top: rect.top,
            bottom: rect.bottom,
            maxHeight: rect.maxHeight,
          }}
        >
          {showSearch && (
            <div className="ss-search-wrap">
              <input
                ref={searchRef}
                className="ss-search"
                value={query}
                onChange={onQuery}
                onKeyDown={onKeyDown}
                placeholder={searchPlaceholder}
                autoComplete="off"
                spellCheck={false}
              />
            </div>
          )}
          <div className="ss-options">
            {canCreate && (
              <div
                className="ss-option ss-create"
                role="option"
                aria-selected={false}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => commit({ value: trimmedQuery, label: trimmedQuery })}
              >
                Use “{trimmedQuery}”
              </div>
            )}
            {filtered.length === 0 && !canCreate && (
              <div className="ss-empty">No matches for “{query}”</div>
            )}
            {filtered.map((opt, i) => (
              <div
                key={`${opt.value}-${i}`}
                data-idx={i}
                role="option"
                aria-selected={opt.value === String(value ?? '')}
                className={
                  'ss-option' +
                  (i === highlight ? ' ss-active' : '') +
                  (opt.value === String(value ?? '') ? ' ss-selected' : '') +
                  (opt.disabled ? ' ss-option-disabled' : '')
                }
                onMouseEnter={() => setHighlight(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => commit(opt)}
              >
                {opt.label || <span className="ss-placeholder">(blank)</span>}
              </div>
            ))}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
